import React, { useEffect, useState } from "react";
import api, { apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader, Loader, StatCard } from "@/components/kit";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { ClipboardCheck, CalendarClock, FileSpreadsheet, Clock, CheckCircle2, XCircle, Loader2, LifeBuoy, Gauge, AlertTriangle, Timer } from "lucide-react";
import { ResponsiveContainer, BarChart, Bar, PieChart, Pie, Cell, XAxis, YAxis, Tooltip, CartesianGrid } from "recharts";

const COLORS = ["#16305B", "#2563EB", "#10B981", "#F59E0B", "#EF4444", "#8B5CF6", "#0EA5E9", "#F97316"];

const VISITOR_STATUSES = [
  ["", "All statuses"], ["PENDING_EMPLOYEE_APPROVAL", "Awaiting Host"], ["HOLD", "On Hold"],
  ["APPROVED_BY_EMPLOYEE", "Approved"], ["VERIFIED", "Verified"], ["CHECKED_IN", "Checked In"],
  ["CHECKED_OUT", "Checked Out"], ["REJECTED_BY_EMPLOYEE", "Rejected (Host)"], ["REJECTED_BY_RECEPTION", "Rejected (Reception)"],
];
const BOOKING_STATUSES = [
  ["", "All statuses"], ["CONFIRMED", "Confirmed"], ["PENDING_APPROVAL", "Pending Approval"],
  ["REJECTED", "Rejected"], ["CANCELLED", "Cancelled"],
];
const TICKET_STATUSES = [
  ["", "All statuses"], ["New", "New"], ["Assigned", "Assigned"], ["In Progress", "In Progress"],
  ["On Hold", "On Hold"], ["Reopened", "Reopened"], ["Resolved", "Resolved"], ["Closed", "Closed"],
];

const toArr = (obj) => Object.entries(obj || {}).map(([name, value]) => ({ name, value })).sort((a, b) => b.value - a.value);
const fmtMins = (m) => { m = Math.round(m || 0); const h = Math.floor(m / 60), mm = m % 60; return h ? `${h}h ${mm}m` : `${mm}m`; };

function Filters({ f, setF, branches, companies, statuses, showBranch, showCompany, onExport, exporting, testid, extra }) {
  const set = (k, v) => setF({ ...f, [k]: v });
  return (
    <div className="bg-white rounded-2xl border border-slate-200 p-4 mb-5 flex flex-wrap items-end gap-3">
      {showCompany && (
        <div className="grow min-w-[150px]">
          <Label className="text-xs text-slate-500">Company</Label>
          <Select value={f.company_id || "__all"} onValueChange={(v) => set("company_id", v === "__all" ? "" : v)}>
            <SelectTrigger className="mt-1 h-9" data-testid={`${testid}-company`}><SelectValue placeholder="All companies" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="__all">All companies</SelectItem>
              {companies.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
      )}
      <div className="grow min-w-[130px]">
        <Label className="text-xs text-slate-500">From</Label>
        <Input type="date" value={f.date_from || ""} onChange={(e) => set("date_from", e.target.value)} className="mt-1 h-9" data-testid={`${testid}-from`} />
      </div>
      <div className="grow min-w-[130px]">
        <Label className="text-xs text-slate-500">To</Label>
        <Input type="date" value={f.date_to || ""} onChange={(e) => set("date_to", e.target.value)} className="mt-1 h-9" data-testid={`${testid}-to`} />
      </div>
      <div className="grow min-w-[150px]">
        <Label className="text-xs text-slate-500">Status</Label>
        <Select value={f.status || "__all"} onValueChange={(v) => set("status", v === "__all" ? "" : v)}>
          <SelectTrigger className="mt-1 h-9" data-testid={`${testid}-status`}><SelectValue /></SelectTrigger>
          <SelectContent>{statuses.map(([v, l]) => <SelectItem key={v || "__all"} value={v || "__all"}>{l}</SelectItem>)}</SelectContent>
        </Select>
      </div>
      {showBranch && (
        <div className="grow min-w-[150px]">
          <Label className="text-xs text-slate-500">Branch</Label>
          <Select value={f.branch_id || "__all"} onValueChange={(v) => set("branch_id", v === "__all" ? "" : v)}>
            <SelectTrigger className="mt-1 h-9" data-testid={`${testid}-branch`}><SelectValue placeholder="All branches" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="__all">All branches</SelectItem>
              {branches.map((b) => <SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
      )}
      {extra}
      <Button onClick={onExport} disabled={exporting} data-testid={`${testid}-export`} className="bg-emerald-600 hover:bg-emerald-700 h-9">
        {exporting ? <Loader2 className="animate-spin" size={15} /> : <><FileSpreadsheet size={15} className="mr-1.5" /> Export Excel</>}
      </Button>
    </div>
  );
}

const ChartCard = ({ title, children }) => (
  <div className="bg-white rounded-2xl border border-slate-200 p-6">
    <h3 className="font-display font-semibold text-navy-900 mb-4">{title}</h3>
    {children}
  </div>
);

const Bars = ({ data, color = "#16305B", dataKey = "value" }) => (
  data.length === 0 ? <p className="text-sm text-slate-400">No data.</p> : (
    <ResponsiveContainer width="100%" height={240}>
      <BarChart data={data}><CartesianGrid strokeDasharray="3 3" stroke="#eef2f7" vertical={false} /><XAxis dataKey="name" tick={{ fontSize: 11, fill: "#94a3b8" }} interval={0} angle={data.length > 5 ? -20 : 0} textAnchor={data.length > 5 ? "end" : "middle"} height={data.length > 5 ? 50 : 30} /><YAxis allowDecimals={false} tick={{ fontSize: 11 }} width={24} /><Tooltip /><Bar dataKey={dataKey} fill={color} radius={[6, 6, 0, 0]} /></BarChart>
    </ResponsiveContainer>
  )
);

export default function Reports() {
  const { user } = useAuth();
  const canPickBranch = ["SUPER_ADMIN", "COMPANY_ADMIN", "IT_ADMIN"].includes(user.role);
  const isSuper = user.role === "SUPER_ADMIN";
  const [branches, setBranches] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [ticketCfg, setTicketCfg] = useState({ categories: [], priorities: [], departments: [] });
  const [vis, setVis] = useState(null);
  const [bk, setBk] = useState(null);
  const [tk, setTk] = useState(null);
  const [vf, setVf] = useState({});
  const [bf, setBf] = useState({});
  const [tf, setTf] = useState({});
  const [exp, setExp] = useState(null);

  const qs = (f) => {
    const p = new URLSearchParams();
    ["company_id", "branch_id", "status", "date_from", "date_to", "department", "category", "priority"].forEach((k) => { if (f[k]) p.set(k, f[k]); });
    return p.toString();
  };

  const loadVis = () => api.get(`/reports/visitors?${qs(vf)}`).then((r) => setVis(r.data)).catch((e) => toast.error(apiError(e)));
  const loadBk = () => api.get(`/reports/bookings?${qs(bf)}`).then((r) => setBk(r.data)).catch(() => {});
  const loadTk = () => api.get(`/reports/tickets?${qs(tf)}`).then((r) => setTk(r.data)).catch((e) => toast.error(apiError(e)));

  useEffect(() => { if (canPickBranch) api.get("/branches").then((r) => setBranches(r.data)).catch(() => {}); }, [canPickBranch]);
  useEffect(() => { if (isSuper) api.get("/companies").then((r) => setCompanies(r.data)).catch(() => {}); }, [isSuper]);
  useEffect(() => { api.get("/ticket-config").then((r) => setTicketCfg(r.data)).catch(() => {}); }, []);
  useEffect(() => { loadVis(); }, [vf.branch_id, vf.company_id]);
  useEffect(() => { loadBk(); }, [bf.branch_id, bf.company_id]);
  useEffect(() => { loadTk(); }, [tf.branch_id, tf.company_id, tf.department, tf.category, tf.priority]);

  const download = async (kind, filterObj, fname) => {
    setExp(kind);
    try {
      const res = await api.get(`/reports/${kind}/export?${qs(filterObj)}`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url; a.download = fname; a.click();
      URL.revokeObjectURL(url);
      toast.success("Excel downloaded");
    } catch (e) { toast.error(apiError(e)); } finally { setExp(null); }
  };

  if (!vis || !bk || !tk) return <Loader />;

  const ticketExtra = (
    <>
      <div className="grow min-w-[140px]">
        <Label className="text-xs text-slate-500">Department</Label>
        <Select value={tf.department || "__all"} onValueChange={(v) => setTf({ ...tf, department: v === "__all" ? "" : v })}>
          <SelectTrigger className="mt-1 h-9" data-testid="tickets-filter-department"><SelectValue placeholder="All" /></SelectTrigger>
          <SelectContent><SelectItem value="__all">All departments</SelectItem>{(ticketCfg.departments || []).map((d) => <SelectItem key={d} value={d}>{d}</SelectItem>)}</SelectContent>
        </Select>
      </div>
      <div className="grow min-w-[140px]">
        <Label className="text-xs text-slate-500">Priority</Label>
        <Select value={tf.priority || "__all"} onValueChange={(v) => setTf({ ...tf, priority: v === "__all" ? "" : v })}>
          <SelectTrigger className="mt-1 h-9" data-testid="tickets-filter-priority"><SelectValue placeholder="All" /></SelectTrigger>
          <SelectContent><SelectItem value="__all">All priorities</SelectItem>{(ticketCfg.priorities || []).map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}</SelectContent>
        </Select>
      </div>
    </>
  );

  return (
    <div>
      <PageHeader title="Reports" subtitle="Unified insights across visitors, meeting rooms and service requests. Filter and export to Excel." />
      <Tabs defaultValue="tickets">
        <TabsList className="mb-5">
          <TabsTrigger value="tickets" data-testid="report-tab-tickets">Service Requests</TabsTrigger>
          <TabsTrigger value="visitors" data-testid="report-tab-visitors">Visitor Reports</TabsTrigger>
          <TabsTrigger value="rooms" data-testid="report-tab-rooms">Meeting Room Reports</TabsTrigger>
        </TabsList>

        <TabsContent value="tickets">
          <Filters f={tf} setF={setTf} branches={branches} companies={companies} statuses={TICKET_STATUSES} showBranch={canPickBranch} showCompany={isSuper}
            onExport={() => download("tickets", tf, "ticket-report.xlsx")} exporting={exp === "tickets"} testid="tickets-filter" extra={ticketExtra} />
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            <StatCard label="Total Tickets" value={tk.total} icon={LifeBuoy} tone="navy" />
            <StatCard label="Open" value={tk.open} icon={Clock} tone="blue" />
            <StatCard label="Resolved" value={tk.resolved} icon={CheckCircle2} tone="green" />
            <StatCard label="Overdue" value={tk.overdue} icon={AlertTriangle} tone="red" />
            <StatCard label="Resolution SLA" value={`${tk.sla_compliance}%`} icon={Gauge} tone="green" />
            <StatCard label="Response SLA" value={`${tk.response_compliance}%`} icon={Timer} tone="blue" />
            <StatCard label="Avg Resolution" value={fmtMins(tk.avg_resolution_minutes)} icon={Clock} tone="navy" />
            <StatCard label="Reopened" value={tk.reopened} icon={XCircle} tone="amber" />
          </div>
          <div className="grid lg:grid-cols-2 gap-6 mt-6">
            <ChartCard title="By Category"><Bars data={toArr(tk.by_category)} color="#16305B" /></ChartCard>
            <ChartCard title="By Department"><Bars data={toArr(tk.by_department)} color="#2563EB" /></ChartCard>
            <ChartCard title="By Priority">
              {toArr(tk.by_priority).length === 0 ? <p className="text-sm text-slate-400">No data.</p> : (
                <ResponsiveContainer width="100%" height={240}><PieChart><Pie data={toArr(tk.by_priority)} dataKey="value" nameKey="name" outerRadius={85} label>{toArr(tk.by_priority).map((e, i) => <Cell key={e.name} fill={COLORS[i % COLORS.length]} />)}</Pie><Tooltip /></PieChart></ResponsiveContainer>
              )}
            </ChartCard>
            <ChartCard title="By Status"><Bars data={toArr(tk.by_status)} color="#10B981" /></ChartCard>
            <ChartCard title="Top Issues"><Bars data={toArr(tk.by_issue).slice(0, 8)} color="#F59E0B" /></ChartCard>
            <ChartCard title="By Branch"><Bars data={toArr(tk.by_branch)} color="#8B5CF6" /></ChartCard>
          </div>
          <div className="bg-white rounded-2xl border border-slate-200 p-6 mt-6">
            <h3 className="font-display font-semibold text-navy-900 mb-4">Support Team Performance</h3>
            {(!tk.support || tk.support.length === 0) ? <p className="text-sm text-slate-400">No assigned tickets yet.</p> : (
              <Table>
                <TableHeader><TableRow className="bg-slate-50"><TableHead>Agent</TableHead><TableHead className="text-right">Assigned</TableHead><TableHead className="text-right">Resolved</TableHead><TableHead className="text-right">Avg Resolution</TableHead></TableRow></TableHeader>
                <TableBody>
                  {tk.support.map((s) => (
                    <TableRow key={s.name} data-testid={`support-perf-${s.name}`}>
                      <TableCell className="font-medium text-navy-900">{s.name}</TableCell>
                      <TableCell className="text-right text-slate-600">{s.assigned}</TableCell>
                      <TableCell className="text-right text-emerald-700 font-semibold">{s.resolved}</TableCell>
                      <TableCell className="text-right text-slate-600">{fmtMins(s.avg_minutes)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </div>
        </TabsContent>

        <TabsContent value="visitors">
          <Filters f={vf} setF={setVf} branches={branches} companies={companies} statuses={VISITOR_STATUSES} showBranch={canPickBranch} showCompany={isSuper}
            onExport={() => download("visitors", vf, "visitor-report.xlsx")} exporting={exp === "visitors"} testid="visitors-filter" />
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            <StatCard label="Total Visitors" value={vis.total} icon={ClipboardCheck} tone="navy" />
            <StatCard label="Verified" value={vis.verified} icon={CheckCircle2} tone="green" />
            <StatCard label="Checked Out" value={vis.checked_out} icon={Clock} tone="blue" />
            <StatCard label="Rejected" value={vis.rejected} icon={XCircle} tone="red" />
            <StatCard label="Checked In" value={vis.checked_in} icon={ClipboardCheck} tone="blue" />
            <StatCard label="On Hold" value={vis.hold} icon={Clock} tone="amber" />
            <StatCard label="Approved" value={vis.approved} icon={CheckCircle2} tone="green" />
            <StatCard label="Avg Duration" value={`${vis.avg_duration_minutes}m`} icon={Clock} tone="navy" />
          </div>
          <div className="bg-white rounded-2xl border border-slate-200 p-6 mt-6">
            <h3 className="font-display font-semibold text-navy-900 mb-4">Visitors by Purpose</h3>
            {vis.by_purpose.length === 0 ? <p className="text-sm text-slate-400">No data.</p> : (
              <ResponsiveContainer width="100%" height={260}>
                <PieChart><Pie data={vis.by_purpose} dataKey="value" nameKey="name" outerRadius={90} label>{vis.by_purpose.map((e, i) => <Cell key={e.name} fill={COLORS[i % COLORS.length]} />)}</Pie><Tooltip /></PieChart>
              </ResponsiveContainer>
            )}
          </div>
        </TabsContent>

        <TabsContent value="rooms">
          <Filters f={bf} setF={setBf} branches={branches} companies={companies} statuses={BOOKING_STATUSES} showBranch={canPickBranch} showCompany={isSuper}
            onExport={() => download("bookings", bf, "booking-report.xlsx")} exporting={exp === "bookings"} testid="bookings-filter" />
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            <StatCard label="Total Bookings" value={bk.total} icon={CalendarClock} tone="navy" />
            <StatCard label="Confirmed" value={bk.confirmed} icon={CheckCircle2} tone="green" />
            <StatCard label="Pending" value={bk.pending} icon={Clock} tone="amber" />
            <StatCard label="Extended" value={bk.extended} icon={CalendarClock} tone="blue" />
          </div>
          <div className="grid lg:grid-cols-2 gap-6 mt-6">
            <ChartCard title="Bookings by Room"><Bars data={bk.by_room} color="#16305B" /></ChartCard>
            <ChartCard title="Peak Hours">
              <ResponsiveContainer width="100%" height={240}>
                <BarChart data={bk.peak_hours}><CartesianGrid strokeDasharray="3 3" stroke="#eef2f7" vertical={false} /><XAxis dataKey="hour" tick={{ fontSize: 11, fill: "#94a3b8" }} /><YAxis allowDecimals={false} tick={{ fontSize: 11 }} width={24} /><Tooltip /><Bar dataKey="count" fill="#2563EB" radius={[6, 6, 0, 0]} /></BarChart>
              </ResponsiveContainer>
            </ChartCard>
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}
